import React, { useState } from "react";
import { useRegTheme } from "../theme";
import { renderReceiptHTML, printReceiptHTML } from "../data/receipt";
import { hasReceiptPrinter, printReceipt } from "../data/printers";
import { phpEmailSmsReceipt } from "../data/api";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";

// Post-payment receipt choice — mirrors Android setPaymentSuccessDialog: Print / Email / SMS / Done.
export default function ReceiptModal({ model, onClose }) {
  const t = useRegTheme();
  const [mode, setMode] = useState(null); // null | "email" | "sms"
  const [dest, setDest] = useState("");
  const [busy, setBusy] = useState(false);
  const html = renderReceiptHTML(model);

  const print = async () => {
    if (model.hasLotteryPayout) return ToastifyAlert("This order includes a lottery redemption, so the receipt cannot be printed.", "error");
    // Prefer the configured receipt printer via QZ (dialog-free raster — "Lightspeed Hub" style).
    // Fall back to the browser print dialog if no printer is set or QZ can't reach it.
    if (hasReceiptPrinter()) {
      const r = await printReceipt(model); // render the model → bitmap → print as image (Android-style)
      if (r.ok) return ToastifyAlert("Receipt sent to printer", "success");
      ToastifyAlert(
        "Printer error (" + (r.reason === "no-bridge" ? "QZ Tray not running" : r.error || r.reason) + ") — opening browser print",
        "error"
      );
    }
    printReceiptHTML(html);
  };
  const send = async () => {
    if (model.hasLotteryPayout) return ToastifyAlert("This order includes a lottery redemption, so the receipt cannot be printed.", "error");
    const to = dest.trim();
    if (!to) return ToastifyAlert(mode === "email" ? "Enter an email" : "Enter a phone number", "error");
    setBusy(true);
    try {
      const res = await phpEmailSmsReceipt({
        orderId: model.orderId,
        type: mode,
        email: mode === "email" ? to : "",
        number: mode === "sms" ? to : "",
        html,
      });
      const ok = res && (res.status === true || res.status === "true" || res.status === 1);
      ToastifyAlert(ok ? `Receipt ${mode === "email" ? "emailed" : "texted"}` : "Could not send the receipt", ok ? "success" : "error");
      if (ok) {
        setMode(null);
        setDest("");
      }
    } catch {
      ToastifyAlert("Could not send the receipt", "error");
    } finally {
      setBusy(false);
    }
  };

  const s = styles(t);
  return (
    <div className="qv-dlg-overlay" style={s.overlay}>
      <div className="qv-dlg-card" style={s.card}>
        <div style={s.head}>Order complete</div>
        <div style={s.preview} dangerouslySetInnerHTML={{ __html: html }} />
        {mode ? (
          <div style={s.body}>
            <div style={s.label}>{mode === "email" ? "Email address" : "Phone number"}</div>
            <input
              style={s.input}
              value={dest}
              onChange={(e) => setDest(e.target.value)}
              placeholder={mode === "email" ? "name@example.com" : "5551234567"}
              autoFocus
            />
            <div style={s.rowBtns}>
              <button style={s.ghost} onClick={() => setMode(null)} disabled={busy}>Back</button>
              <button style={s.primary} onClick={send} disabled={busy}>{busy ? "…" : "Send"}</button>
            </div>
          </div>
        ) : (
          <div style={s.grid}>
            <button style={s.opt} onClick={print}>🖨 Print</button>
            <button style={s.opt} onClick={() => setMode("email")}>✉ Email</button>
            <button style={s.opt} onClick={() => setMode("sms")}>💬 Text</button>
            <button style={s.done} onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = (t) => ({
  overlay: { position: "fixed", inset: 0, background: "rgba(2,6,23,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2400 },
  card: { width: "100%", maxWidth: 420, maxHeight: "92vh", background: t.card || "#0f1a2f", color: t.text || "#E5EDF7", borderRadius: 16, border: `1px solid ${t.cardBorder || "#1f2c47"}`, overflow: "hidden", display: "flex", flexDirection: "column" },
  head: { padding: "14px 18px", fontWeight: 800, fontSize: 16, borderBottom: `1px solid ${t.divider}`, background: "rgba(5,150,105,0.15)", color: "#10B981" },
  // The receipt is rendered as PAPER (hard-coded #000 text in renderReceiptHTML), so the preview is
  // always white — on the dark themes var(--panel-2) put black text on a near-black panel (QA 08-27).
  preview: { flex: 1, overflow: "auto", background: "#fff", color: "#000", colorScheme: "light", padding: 14, margin: 14, borderRadius: 8, border: "1px solid var(--line)" },
  body: { padding: "0 18px 18px", display: "flex", flexDirection: "column", gap: 10 },
  label: { fontSize: 13, color: t.textMuted },
  input: { width: "100%", height: 46, borderRadius: 10, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.inputText, padding: "0 14px", fontSize: 16, outline: "none", boxSizing: "border-box" },
  rowBtns: { display: "flex", gap: 10 },
  grid: { padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  opt: { height: 52, borderRadius: 12, border: `1px solid ${t.cardBorder}`, background: t.chipBg, color: t.text, fontWeight: 500, fontSize: 15, cursor: "pointer" },
  done: { height: 52, borderRadius: 12, border: "none", background: "#059669", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer", gridColumn: "1 / -1" },
  ghost: { flex: 1, height: 48, borderRadius: 12, border: `1px solid ${t.cardBorder}`, background: "transparent", color: t.text, fontWeight: 500, cursor: "pointer" },
  primary: { flex: 1.5, height: 48, borderRadius: 12, border: "none", background: t.accent, color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer" },
});
